import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type { Log } from '@blakeelearning/log'
import type RouterService from '@ember/routing/router-service'
import Service, { service } from '@ember/service'
import type { NormalizedInterval } from 'date-fns'
import config from 'district-ui-client/config/environment'
import type FlashQueueService from 'district-ui-client/services/flash-queue'
import { join, joinQueryParams } from 'district-ui-client/utils/uri'
import type { IntlService } from 'ember-intl'

interface StudentSummaryResponse {
  message: string
}

export default class EmailAllStudentsCsvService extends Service {
  @service flashQueue!: FlashQueueService

  @service log!: Log

  @service router!: RouterService

  @service intl!: IntlService

  @service authToken!: AuthToken

  buildEmailAllStudentsCSVEndpoint(districtId: string, product: string, period: NormalizedInterval) {
    // RE Rails API still treats ReadingEggs as product: 'reading' and not 're'
    // This is a temporary modification till the RE Rails API is brought upto date
    let legacyProduct = product
    if (product === 're') {
      legacyProduct = 'reading'
    }
    const { start, end } = period
    const url = join(config.readingEggsV1Url, 'district_ui', 'reporting', 'students_summary')
    return joinQueryParams(url, {
      district_id: districtId,
      product: legacyProduct,
      from_date: start.toISOString(),
      to_date: end.toISOString(),
    })
  }

  async emailAllStudentsCSV(districtId: string, product: string, period: NormalizedInterval) {
    const uri = this.buildEmailAllStudentsCSVEndpoint(districtId, product, period)

    const { flashQueue, log, router, intl } = this
    const contactUsUrl = router.urlFor('subscription-type.support')

    const response = await fetch(uri, {
      method: 'GET',
      headers: { Authorization: this.authToken.token ?? '', Accept: 'application/json' },
    })

    if (response.ok) {
      const json = (await response.json()) as StudentSummaryResponse
      const title = json?.message ?? ''
      const subtitle = intl.t('flashBox.positiveContactUs', { contactUsUrl, htmlSafe: true })

      flashQueue.addSuccess({ title, subtitle })
    } else {
      const title = intl.t('reporting.emailCsv.errorFlash')
      const subtitle = intl.t('flashBox.criticalContactUs', { contactUsUrl, htmlSafe: true })

      flashQueue.addFail({ title, subtitle })
      log.error(title, { backendError: await response.text() })
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'email-all-students-csv': EmailAllStudentsCsvService
  }
}
